import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import IconButton from 'components/IconButton';
import LoadingScreen from 'components/LoadingScreen';
import Markdown from 'components/Markdown';
import ModalDialog from 'components/ModalDialog';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import { useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
} from 'reactstrap';

import { forceRerenderFindingGridAtom } from './state';
import GenericRichEditor from '../../components/GenericRichEditor/GenericRichEditor';

type Props = {
  finding: any;
  onClose: () => void;
};

const FindingNotesModal = ({ finding, onClose }: Props) => {
  const [notes, setNotes] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [editorEditedState, setEditorEditedState] = useState(
    EditorState.createEmpty(),
  );

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [noteToDeleteId, setNoteToDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editedNoteId, setEditedNoteId] = useState(null);

  const forceRenderGrid = useSetAtom(forceRerenderFindingGridAtom);

  // useEffect
  useEffect(() => {
    if (!finding) return;
    setLoading(true);
    callApi(`/notes/?finding_id=${finding.id}`, 'GET')
      .then((res) => {
        if (res.status === 200) {
          setNotes(res.data);
        } else {
          // eslint-disable-next-line no-console
          setError('Error fetching finding detail');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [finding]);

  const handleDeleteNotes = (noteId) => {
    setLoadingDelete(true);
    callApi(`/notes/${noteId}/delete/`, 'DELETE')
      .then((res) => {
        if (res.status === 204) {
          setShowConfirmDeleteModal(false);
          setNotes((prevNotes) => ({
            ...prevNotes,
            results: prevNotes.results.filter((note) => note.id !== noteId),
          }));
          setNoteToDeleteId(null);
          finding['notes_count'] = finding['notes_count'] - 1;
          forceRenderGrid(Math.random().toString());
          showSuccessToast('Note deleted successfully');
        } else {
          showFailureToast('Error deleting note');
        }
      })
      .catch((err) => {
        showFailureToast(err.data.detail);
      })
      .finally(() => {
        setLoadingDelete(false);
      });
  };

  const handleEditNote = (note) => {
    setIsEditing(true);
    // Convert markdown to draft-js ContentState
    const rawContentState = markdownToDraft(note.data);
    const contentState = convertFromRaw(rawContentState);
    setEditorEditedState(EditorState.createWithContent(contentState));
    setEditedNoteId(note.id);
  };

  const handleSaveEditedNote = () => {
    const content = editorEditedState.getCurrentContent();
    const rawContent = convertToRaw(content);
    const markdownString = draftToMarkdown(rawContent);
    const data = {
      data: markdownString,
    };

    callApi(`/notes/${editedNoteId}/`, 'PATCH', {}, data)
      .then((res) => {
        if (res.status === 200) {
          setNotes((prevNotes) => ({
            ...prevNotes,
            results: prevNotes.results.map((note) => {
              if (note.id === editedNoteId) {
                return { ...note, data: res.data.data };
              }
              return note;
            }),
          }));
          setIsEditing(false);
          showSuccessToast('Note updated successfully');
        } else {
          showFailureToast('Error updating note');
        }
      })
      .catch((err) => {
        showFailureToast(err.data.detail);
      });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const renderDeleteConfirmModal = () => {
    return (
      <Modal isOpen={showConfirmDeleteModal} size="sm" zIndex={9999}>
        <ModalHeader>
          <h4>Delete Confirmation</h4>
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete this Note?</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowConfirmDeleteModal(false)}
          >
            Cancel
          </Button>
          {loadingDelete ? (
            <CircleSpinner size={20} color="#0665a9" loading={true} />
          ) : (
            <Button
              color="danger"
              disabled={loadingDelete}
              onClick={() => handleDeleteNotes(noteToDeleteId)}
            >
              Delete
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  };

  const renderNotesContent = () => {
    let noteItems = notes.results;
    if (noteItems === undefined) noteItems = [];
    let data = noteItems.map((item, oindex) => {
      return (
        <>
          <Row
            className="p-2"
            key={oindex}
            style={{ marginBottom: '15px', position: 'relative' }}
          >
            <Col>
              <Row>
                <Col md="10" className="spaced">
                  <i className="custom-icon-text spaced">{item.initials}</i>
                  {item.name} added a note on{' '}
                  {moment(item.created_at).format('lll')}
                </Col>

                <Col md="2" className="text-right">
                  {isEditing && editedNoteId === item.id ? (
                    <>
                      <IconButton
                        iconClass="fas fa-check"
                        tooltip="Update Note"
                        onClick={handleSaveEditedNote}
                        id="save-note-button"
                      />
                      <IconButton
                        iconClass="fas fa-times"
                        tooltip="Discard Changes"
                        onClick={handleCancelEdit}
                        id="cancel-note-button"
                      />
                    </>
                  ) : (
                    <>
                      <IconButton
                        iconClass="fas fa-edit"
                        tooltip={
                          item.email === localStorage.getItem('loggedInUser')
                            ? 'Edit Note'
                            : "You can't modify other user's notes"
                        }
                        tooltipPlacement="left"
                        onClick={() => handleEditNote(item)}
                        id="edit-note-button"
                        disabled={
                          item.email !== localStorage.getItem('loggedInUser')
                        }
                      />
                      <IconButton
                        iconClass="fas fa-trash"
                        tooltip={
                          item.email === localStorage.getItem('loggedInUser')
                            ? 'Delete Note'
                            : "You can't delete other users's notes"
                        }
                        tooltipPlacement="left"
                        onClick={() => {
                          setShowConfirmDeleteModal(true);
                          setNoteToDeleteId(item.id);
                        }}
                        id="delete-note-button"
                        disabled={
                          item.email !== localStorage.getItem('loggedInUser')
                        }
                      />
                    </>
                  )}
                </Col>
              </Row>

              {/* Content Row */}
              <Row className="note-content-row">
                <Col>
                  {isEditing && editedNoteId === item.id ? (
                    <div className="note-editor-container">
                      <GenericRichEditor
                        editorState={editorEditedState}
                        setEditorState={setEditorEditedState}
                        placeholder="Edit your note..."
                      />
                    </div>
                  ) : (
                    <div style={{ marginTop: '15px' }}>
                      <Markdown>{item.data}</Markdown>
                    </div>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          {showConfirmDeleteModal && renderDeleteConfirmModal()}
        </>
      );
    });

    return (
      <Row>
        {data && data.length > 0 ? (
          <Col md="12">{data}</Col>
        ) : (
          <Col md="12">No existing notes for this vulnerability</Col>
        )}
      </Row>
    );
  };

  const capitalizeWords = (str) => {
    return str.toLowerCase().replace(/(?:^|\s)\w/g, function (word) {
      return word.toUpperCase();
    });
  };

  const handleSaveNotes = () => {
    // eslint-disable-next-line no-console
    const content = editorState.getCurrentContent();
    const rawContent = convertToRaw(content);
    const markdownString = draftToMarkdown(rawContent);
    const data = {
      finding_id: finding.id,
      data: markdownString,
    };

    const url = '/notes/?finding_id=' + finding.id;

    callApi(url, 'POST', {}, data)
      .then((res) => {
        if (res.status === 201) {
          setNotes((prevNotes) => ({
            ...prevNotes,
            results: [...prevNotes.results, res.data],
          }));
          setEditorState(EditorState.createEmpty());

          // update the finding notes count
          finding['notes_count'] = finding['notes_count'] + 1;
          showSuccessToast('Note saved successfully');
          forceRenderGrid(Math.random().toString());
        } else {
          showFailureToast('An error occurred while saving the note');
        }
      })
      .catch(() => {
        showFailureToast('An error occurred while saving the note');
      });
  };

  return (
    <ModalDialog
      title={`${
        notes.issue_type ? capitalizeWords(notes.issue_type) : 'Vulnerability'
      }  Notes`}
      subTitle={finding['name'] || ''}
      childComponent={
        <CustomModalWrapper id="notes-scroll">
          <Row>
            <Col>
              {error && <div>{error}</div>}
              {loading && <LoadingScreen />}
              {!error && !loading && (
                <>
                  <br />
                  <h5>Existing Notes</h5>
                  <CustomModalWrapper id="existing-notes-scroll" height="40vh">
                    {notes && notes.results ? (
                      renderNotesContent()
                    ) : (
                      <div>No existing notes for this vulnerability</div>
                    )}
                  </CustomModalWrapper>
                  <Card className="editorContainer">
                    <br />
                    <h5>Add New Note</h5>
                    <GenericRichEditor
                      editorState={editorState}
                      setEditorState={setEditorState}
                      placeholder="Add your note..."
                    />
                  </Card>
                </>
              )}
            </Col>
          </Row>
        </CustomModalWrapper>
      }
      customFooter={
        <Row className="w-100">
          <Col className="text-left">
            <Button color="secondary" onClick={onClose}>
              Close
            </Button>
          </Col>
          <Col className="text-right">
            <Button
              color="primary"
              onClick={handleSaveNotes}
              disabled={isEditing}
            >
              Add Note
            </Button>
          </Col>
        </Row>
      }
      onClose={() => onClose()}
    />
  );
};

export default FindingNotesModal;
